/**
 * The assistant's conversation and composer, on `@mui/x-chat`'s `ChatBox`.
 * The panel's shell (header, drag/resize, access gate) is `AgentPanel`; the
 * mapping to/from our own chat state is `agentChatAdapter.js`. `useAgentChat`
 * (owned by `AgentPanel`, passed in as `chat`) still does the fetching, the
 * message capping, the view memory and the refresh-after-write — this
 * component only renders MUI X Chat driven by the same event stream.
 */
import {
  CheckCircle as FinishedIcon,
  Close as CloseIcon,
  DeleteOutlined as DeleteIcon,
  EditOutlined as EditIcon,
  Mic as MicIcon,
  MicNone as MicOffIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  Replay as ReplayIcon,
  Stop as StopIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import {
  ChatComposerAttachButton,
  type ChatComposerAttachButtonProps,
  ChatConfirmation,
  ChatSuggestions,
  type ChatSuggestionsProps,
  type ChatMessageActionsResolveContext,
  type ChatMessageExtraAction,
  ChatBox,
  ChatConversationList,
  chatConversationListClasses,
  chatMessageClasses,
} from "@mui/x-chat";
import {
  type ChatAttachmentRejection,
  type ChatConversation,
  type ChatMessage,
  type ChatPartRendererMap,
  type ConversationListTitleProps,
  StreamingIndicator,
  type StreamingIndicatorProps,
  useChat,
  useChatComposer,
  useChatStore,
  useConversations,
} from "@mui/x-chat/headless";
import { type ApolloClient, useApolloClient } from "@apollo/client";
import { getDataOrThrow } from "@msys/common";
import { useFormatting } from "@msys/ui";
import * as Sentry from "@sentry/react";
import { useTolgee, useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import {
  ConfirmProcess,
  type ConfirmProcessRef,
} from "../../commons/modals/ConfirmProcess.js";
import { MAX_ATTACHMENT_BYTES, MAX_ATTACHMENTS } from "./AgentAttachments.js";
import type {
  AgentBackgroundTurn,
  AgentBackgroundTurns,
} from "./agentBackgroundTurns.js";
import {
  AgentConversationsDocument,
  useDeleteAgentConversationMutation,
  useRenameAgentConversationMutation,
  useRequestAgentAttachmentUploadUrlMutation,
  type AgentConversationsQuery,
} from "./AgentChatBox.generated.js";
import {
  conversationListSubtitle,
  createAgentChatAdapter,
  metaOf,
  resolveAnswerCostUsd,
  runningAnswerMessage,
  stripLeadingBreak,
  toChatMessages,
  viewOf,
  visibleText,
  DISCARD_PART,
  META_PART,
  VIEW_PART,
  type AgentMetaPart,
} from "./agentChatAdapter.js";
import { useAgentChatLocaleText } from "./agentChatLocaleText.js";
import { agentUserBubbleBackground } from "./agentSurfaceColors.js";
import { AgentMarkdown } from "./AgentMarkdown.js";
import { useAgentToolTitle } from "./agentToolTitle.js";
import { AgentWorkingStatus } from "./AgentWorkingStatus.js";
import { useDictation } from "./agentDictation.js";
import {
  resolveStoredSpeechCostUsd,
  splitAnswerCost,
} from "./agentSpeechCost.js";
import type { SpeechOutput } from "./agentSpeechOutput.js";
import {
  messageSpeechAction,
  messageSpeechLabelKind,
} from "./agentSpeechPlayback.js";
import { offersYesNo, riskyQuestion } from "./quickReplies.js";
import type { PageContext } from "./pageContext.js";
import { useAgentStarterSuggestions } from "./starters/agentStarters.js";
import { agentStarterRegistry } from "./starters/registry.js";
import type { useAgentChat } from "./useAgentChat.js";
import { agentMessageFooters } from "./views/messageFooters.js";
import { agentUserMessageText } from "./views/userMessageDisplays.js";
import { AgentViewCard } from "./views/AgentViewCard.js";

/** Lets a caller outside the box (the panel's drop zone, `ask()`, a hand-off) send or attach a file. */
export type AgentChatOutbox = {
  send: ((text: string) => void) | null;
  /** A request that arrived before the box mounted; sent once it does. */
  pending: string | null;
  addFiles: ((files: File[]) => void) | null;
  /**
   * Submits the composer as pressing send does — text and attachments (the
   * error line's retry: the message of a failed turn was put back there). A
   * no-op while the composer is empty.
   */
  retry: (() => void) | null;
  /** Shows a stored conversation in the mounted box (the finished notice's "Open"). */
  switchConversation: ((conversationId: string) => void) | null;
  /** A conversation to show that was asked for before the box mounted; shown once it does. */
  pendingConversation: string | null;
};

function OutboxBridge({ outbox }: { outbox: { current: AgentChatOutbox } }) {
  const { sendMessage, setActiveConversation } = useChat();
  const { addAttachment, submit } = useChatComposer();
  React.useEffect(() => {
    outbox.current.addFiles = files => files.forEach(addAttachment);
    return () => {
      outbox.current.addFiles = null;
    };
  }, [outbox, addAttachment]);
  React.useEffect(() => {
    const send = (text: string) =>
      void sendMessage({ parts: [{ type: "text", text }] });
    outbox.current.send = send;
    const pending = outbox.current.pending;
    if (pending !== null) {
      outbox.current.pending = null;
      send(pending);
    }
    return () => {
      outbox.current.send = null;
    };
  }, [outbox, sendMessage]);
  React.useEffect(() => {
    outbox.current.retry = () => void submit();
    return () => {
      outbox.current.retry = null;
    };
  }, [outbox, submit]);
  React.useEffect(() => {
    const switchConversation = (conversationId: string) =>
      void setActiveConversation(conversationId);
    outbox.current.switchConversation = switchConversation;
    const pending = outbox.current.pendingConversation;
    if (pending !== null) {
      outbox.current.pendingConversation = null;
      switchConversation(pending);
    }
    return () => {
      outbox.current.switchConversation = null;
    };
  }, [outbox, setActiveConversation]);
  return null;
}

/** MUI X Chat's store — its type is not exported from the package entry. */
type ChatStore = ReturnType<typeof useChatStore<string>>;

/**
 * What the conversation list needs besides the chat store: the panel's
 * running turns (`agentBackgroundTurns.ts`), where to report which
 * conversations the server says are running, the conversations being
 * deleted right now (already gone from the list, never listed again while
 * the delete runs) and where to hand a deleted one to `useAgentChat`.
 */
type ConversationListState = {
  backgroundTurns: AgentBackgroundTurns;
  reportServerRunning: (ids: ReadonlySet<string>) => void;
  deleting: Set<string>;
  forgetConversation: (conversationId: string) => void;
  /** Titles the user set here, by conversation id: they win over every re-read list. */
  localTitles: Map<string, string>;
  /** Mirrors the title being edited inline, for the reload (state would make it re-run on every keystroke-free edit change). */
  editingRef: React.MutableRefObject<ConversationTitleEdit | null>;
};

/** Which title is edited inline, and where (history row or chat header) — one at a time. */
type ConversationTitleEdit = { id: string; where: "row" | "header" };

const ConversationTitleEditContext = React.createContext<{
  editing: ConversationTitleEdit | null;
  setEditing: (edit: ConversationTitleEdit | null) => void;
}>({ editing: null, setEditing: () => undefined });

const ConversationListStateContext =
  React.createContext<ConversationListState | null>(null);

/**
 * The conversation list as MUI X Chat shows it — shared by the adapter's
 * `listConversations` (first load), every reload (a delete, a finished
 * turn) and `ConversationListLocaleSync` (a language switch re-formats each
 * entry's date). The chat store *replaces* its list with this, so a chat
 * still running here that the server does not list (yet) is folded in
 * first — otherwise a load that resolves after its provisional entry was
 * added would drop it again. Reports the server's `running` flags.
 */
async function fetchConversationList(
  apolloClient: ApolloClient<object>,
  getFormattedDateTime: (value: string) => string,
  listState: ConversationListState
): Promise<ChatConversation[]> {
  const { data } = await apolloClient.query<AgentConversationsQuery>({
    query: AgentConversationsDocument,
    fetchPolicy: "network-only",
  });
  const result = getDataOrThrow(data.agentConversations);
  listState.reportServerRunning(serverRunningIds(result.conversations));
  const listed: ChatConversation[] = result.conversations
    .filter(c => !listState.deleting.has(c.id))
    .map(c => ({
      id: c.id,
      title: listState.localTitles.get(c.id) ?? c.title,
      subtitle: conversationListSubtitle(c, getFormattedDateTime),
      lastMessageAt: c.updatedAt,
    }));
  // Newest first, above everything the server lists: a new chat goes on top.
  const provisional = listState.backgroundTurns
    .provisionalConversations(new Set(listed.map(c => c.id)))
    .filter(c => !listState.deleting.has(c.id))
    .map(provisionalListEntry);
  return [...provisional, ...listed];
}

/** Which listed conversations the server reports as running right now. */
function serverRunningIds(
  conversations: ReadonlyArray<{ id: string; running: boolean }>
): ReadonlySet<string> {
  return new Set(conversations.flatMap(c => (c.running ? [c.id] : [])));
}

/** A running chat's history entry until the server lists it. */
function provisionalListEntry(entry: {
  id: string;
  title: string;
}): ChatConversation {
  return {
    id: entry.id,
    title: entry.title,
    lastMessageAt: new Date().toISOString(),
  };
}

function useConversationListState(): ConversationListState {
  const listState = React.useContext(ConversationListStateContext);
  if (!listState)
    throw new Error("The conversation list is rendered outside AgentChatBox.");
  return listState;
}

/**
 * Re-reads the conversation list into MUI X Chat's store. Replaces the
 * headless `useChat().reloadConversations`, which is only an API stub in
 * this alpha (it throws outside production and does nothing in it).
 */
function useReloadConversationList() {
  const apolloClient = useApolloClient();
  const store = useChatStore();
  const { getFormattedDateTime } = useFormatting();
  const listState = useConversationListState();
  return React.useCallback(async () => {
    const conversations = await fetchConversationList(
      apolloClient,
      getFormattedDateTime,
      listState
    );
    // A title being edited right now stays as it is on screen.
    const editingId = listState.editingRef.current?.id;
    store.setConversations(
      conversations.map(conversation => {
        const shown =
          conversation.id === editingId
            ? store.state.conversationsById[conversation.id]?.title
            : undefined;
        return shown === undefined
          ? conversation
          : { ...conversation, title: shown };
      })
    );
  }, [apolloClient, getFormattedDateTime, store, listState]);
}

/**
 * The list entries' subtitles carry a date formatted when the list was
 * loaded: re-load it once the formatter changes (a language switch), so
 * they follow the language like everything else.
 */
function ConversationListLocaleSync({ enabled }: { enabled: boolean }) {
  const reload = useReloadConversationList();
  const isFirst = React.useRef(true);
  React.useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    if (!enabled) return;
    reload().catch((err: unknown) => Sentry.captureException(err));
  }, [enabled, reload]);
  return null;
}

/**
 * Keeps the box in step with the panel's running turns
 * (`agentBackgroundTurns.ts`):
 * - a chat whose turn just named its conversation (the send mutation's
 *   answer) is listed at once, on top, before the server's list carries it,
 * - the list is re-read once a turn ends (the server's own title and
 *   message count replace the provisional entry, the running marker goes),
 * - a running turn reopened from the history (attached, but no longer
 *   streamed into this box) has its answer shown growing live, then
 *   finished — in place of the stream the box that sent it had.
 */
function BackgroundTurnsSync({
  backgroundTurns,
  toolTitle,
  emptyReplyText,
}: {
  backgroundTurns: AgentBackgroundTurns;
  toolTitle: () => Parameters<typeof runningAnswerMessage>[1];
  emptyReplyText: () => string;
}) {
  const store = useChatStore();
  const reloadConversationList = useReloadConversationList();
  const { deleting } = useConversationListState();
  const titleRefreshTimers = React.useRef<number[]>([]);
  React.useEffect(
    () => () => titleRefreshTimers.current.forEach(window.clearTimeout),
    []
  );
  React.useEffect(() => {
    const showAnswer = (turn: AgentBackgroundTurn) => {
      if (!turn.attached || turn.foreground || !turn.conversationId) return;
      if (store.state.activeConversationId !== turn.conversationId) return;
      const message = runningAnswerMessage(turn, toolTitle(), emptyReplyText);
      const shown: ChatMessage =
        turn.status === "failed" ? { ...message, status: "error" } : message;
      if (store.state.messagesById[shown.id])
        store.updateMessage(shown.id, shown);
      else store.addMessage(shown);
    };
    const listNew = (turn: AgentBackgroundTurn) => {
      const id = turn.conversationId;
      if (turn.status !== "running" || !id) return;
      if (store.state.conversationsById[id] || deleting.has(id)) return;
      // The backend replaces the placeholder title (the first line) with an
      // AI summary a few seconds later: read the list once more then.
      titleRefreshTimers.current.push(
        window.setTimeout(
          () =>
            reloadConversationList().catch((err: unknown) =>
              Sentry.captureException(err)
            ),
          AI_TITLE_REFRESH_DELAY_MS
        )
      );
      store.setConversations([
        provisionalListEntry({ id, title: turn.title }),
        ...store.state.conversationIds.flatMap(existing => {
          const conversation = store.state.conversationsById[existing];
          return conversation ? [conversation] : [];
        }),
      ]);
    };
    return backgroundTurns.subscribe(turn => {
      if (!turn) return;
      listNew(turn);
      showAnswer(turn);
      if (turn.status !== "running")
        reloadConversationList().catch((err: unknown) =>
          Sentry.captureException(err)
        );
    });
  }, [
    backgroundTurns,
    deleting,
    emptyReplyText,
    reloadConversationList,
    store,
    toolTitle,
  ]);
  return null;
}

/** How long after a new conversation's id is known its AI title is fetched once more. */
const AI_TITLE_REFRESH_DELAY_MS = 8000;

/**
 * A history entry's state: its answer is still being written (`running`),
 * or finished in the background and was not opened since (`finished`).
 */
type ConversationStatus = "running" | "finished" | null;

const ConversationStatusContext = React.createContext<
  (conversationId: string) => ConversationStatus
>(() => null);

/** A spinner while the chat runs, a green check for an answer not opened yet — each labelled for screen readers. */
function ConversationStatusIcon({
  conversationId,
}: {
  conversationId: string;
}) {
  const { t } = useTranslate(["Assistant"]);
  const statusOf = React.useContext(ConversationStatusContext);
  const status = statusOf(conversationId);
  if (status === "running")
    return (
      <CircularProgress size={14} thickness={6} aria-label={t("Running…")} />
    );
  if (status === "finished")
    return (
      <FinishedIcon
        color="success"
        titleAccess={t("Answer ready")}
        sx={{ fontSize: 16 }}
      />
    );
  return null;
}

/**
 * A history entry's title, led by its status icon (`ConversationStatusIcon`)
 * — the compact list shows nothing but this title slot, so the icon has to
 * live here. A fixed-width slot keeps the titles aligned.
 */
function ConversationTitleWithStatus({
  conversation,
  selected: _selected,
  unread: _unread,
  focused: _focused,
  slots: _slots,
  slotProps: _slotProps,
  ...rootProps
}: ConversationListTitleProps) {
  const { ownerState: _ownerState, ...divProps } =
    rootProps as typeof rootProps & {
      ownerState?: unknown;
    };
  const { editing } = React.useContext(ConversationTitleEditContext);
  const isEditing = editing?.id === conversation.id && editing.where === "row";
  return (
    <div {...divProps} style={{ ...divProps.style, minWidth: 0 }}>
      <Stack
        direction="row"
        spacing={0.75}
        sx={{ alignItems: "center", minWidth: 0 }}
      >
        <Box
          sx={{
            width: 16,
            height: 16,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ConversationStatusIcon conversationId={conversation.id} />
        </Box>
        {isEditing ? (
          <ConversationTitleEditor conversation={conversation} />
        ) : (
          // The row truncates long titles; hovering shows the whole title.
          <Tooltip
            title={conversation.title ?? conversation.id}
            placement="bottom-start"
            enterDelay={400}
            enterNextDelay={400}
          >
            <Typography
              variant="body2"
              noWrap
              component="span"
              data-testid="conversation-title"
              sx={{ flex: 1, minWidth: 0 }}
            >
              {conversation.title ?? conversation.id}
            </Typography>
          </Tooltip>
        )}
      </Stack>
    </div>
  );
}

/** The shown turn, for the composer's stop button. */
type ShownTurnControls = {
  running: boolean;
  stopping: boolean;
  stop: () => void;
};

const ShownTurnContext = React.createContext<ShownTurnControls>({
  running: false,
  stopping: false,
  stop: () => undefined,
});

/** Next to the composer while the shown answer is being written: asks the assistant to stop it. */
function StopAnswerButton() {
  const { t } = useTranslate(["Assistant"]);
  const { running, stopping, stop } = React.useContext(ShownTurnContext);
  if (!running) return null;
  return (
    <Tooltip title={t("Stop answer")}>
      <span>
        <IconButton
          size="small"
          aria-label={t("Stop answer")}
          disabled={stopping}
          onMouseDown={keepComposerFocus}
          onClick={event => {
            stop();
            focusComposer(event.currentTarget);
          }}
        >
          <StopIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
}

/** Hands MUI X Chat's store to code outside its render tree (the adapter's conflict handling). */
function ChatStoreBridge({
  storeRef,
}: {
  storeRef: React.MutableRefObject<ChatStore | null>;
}) {
  const store = useChatStore();
  React.useEffect(() => {
    storeRef.current = store;
    return () => {
      storeRef.current = null;
    };
  }, [store, storeRef]);
  return null;
}

/**
 * Lets `AttachAndDictate` (a composer slot, rendered deep inside `ChatBox`'s
 * own render tree — not a plain child we could pass a prop to, since the
 * slot API only forwards `ChatComposerAttachButtonProps`) report that the
 * question just sent was dictated, without threading a ref through that
 * API. Consumed by `AgentChatBox` to arm the next streaming answer's
 * autoread (see `SpeechAutoReadSync`) — a no-op default so a box rendered
 * without speech available still works.
 */
const SpokenQuestionSentContext = React.createContext<() => void>(
  () => undefined
);

/**
 * Points at the running dictation's `cancel` (kept current by
 * `AttachAndDictate`), so the adapter's `onSend` — which runs inside the
 * composer's own submit — ends the dictation synchronously, before a result
 * the microphone still delivers can land in the field the submit emptied.
 * A ref, not state: nothing re-renders on it.
 */
const DictationCancelContext = React.createContext<
  React.MutableRefObject<() => void>
>({ current: () => undefined });

/**
 * For the composer's own buttons (attach, microphone, stop): a mouse press
 * must not move the focus off the composer's text field — otherwise, while
 * dictating (the field fills by speech, not typing), Enter would press the
 * focused button again instead of sending. Keyboard users who tab to a
 * button are unaffected: that never fires `mousedown`.
 */
function keepComposerFocus(event: React.MouseEvent) {
  event.preventDefault();
}

/** Returns the focus to the composer's text field the `button` belongs to. */
function focusComposer(button: HTMLElement) {
  const scope = button.closest("form") ?? button.parentElement?.parentElement;
  scope?.querySelector<HTMLTextAreaElement>("textarea")?.focus();
}

/**
 * Reads each new streaming answer aloud as it is written, but only when it
 * answers a dictated question (`autoReadNext`, armed once by
 * `SpokenQuestionSentContext`'s consumer): begins a live session once the
 * newest assistant message starts streaming, feeds it its growing visible
 * text on every render, and flushes/books it once the message is no longer
 * streaming (`speech.finishLiveStream`). Also covers an answer that streamed
 * too fast for the branch above to ever engage (e.g. an instant/cached
 * reply): reads it whole once it's already complete. `autoReadSessionKey`
 * records which message this armed reading is (or was last) for, so pausing
 * precisely that message — and only that one — turns autoread off
 * (`AgentChatBox`'s per-message "Read aloud" action).
 */
function SpeechAutoReadSync({
  speech,
  autoReadNext,
  autoReadSessionKey,
}: {
  speech: SpeechOutput;
  /** Armed at send-time when the question was dictated; consumed once here. */
  autoReadNext: React.MutableRefObject<boolean>;
  autoReadSessionKey: React.MutableRefObject<string | null>;
}) {
  const { messages, isStreaming } = useChat();
  const last = messages[messages.length - 1];
  const wasStreaming = React.useRef(false);

  React.useEffect(() => {
    if (!last || last.role !== "assistant") {
      wasStreaming.current = false;
      return;
    }
    const text = visibleText(last);
    const isThisOne = speech.isActiveKey(last.id);
    if (isStreaming) {
      if (isThisOne) {
        speech.continueLiveStream(text);
      } else if (autoReadNext.current) {
        autoReadNext.current = false;
        autoReadSessionKey.current = last.id;
        speech.beginLiveStream(last.id);
        speech.continueLiveStream(text);
      }
    } else if (wasStreaming.current) {
      // The message that was streaming just finished.
      const meta = metaOf(last);
      if (isThisOne) {
        speech.finishLiveStream(text, meta?.messageId ?? null);
      } else {
        // A reading stopped or replaced mid-answer left usage to book.
        speech.settleUsage(last.id, meta?.messageId ?? null);
        if (autoReadNext.current) {
          autoReadNext.current = false;
          autoReadSessionKey.current = last.id;
          speech.readAloud(last.id, text, meta?.messageId ?? null, false);
        }
      }
    }
    wasStreaming.current = isStreaming;
  }, [autoReadNext, autoReadSessionKey, isStreaming, last, speech]);

  return null;
}

/**
 * A view card whose buttons send chat messages through the box. Once its own
 * message is no longer the newest one, passes `answeredWith` (the following
 * message's text, when that is a user message) so the view can render
 * read-only instead (only `choice` currently uses it).
 */
function ViewPart({
  view,
  message,
  confirmNext,
}: {
  view: ReturnType<typeof viewOf>;
  message: ChatMessage;
  confirmNext: () => void;
}) {
  const { sendMessage, isStreaming, messages } = useChat();
  if (!view) return null;
  const isNewest = messages[messages.length - 1]?.id === message.id;
  const next = isNewest
    ? undefined
    : messages[messages.findIndex(m => m.id === message.id) + 1];
  const answeredWith = isNewest
    ? undefined
    : next?.role === "user"
      ? next.parts
          .flatMap(p => (p.type === "text" ? [p.text] : []))
          .join("\n\n")
      : "";
  return (
    <AgentViewCard
      view={view}
      busy={isStreaming}
      answeredWith={answeredWith}
      onSend={(text, options) => {
        if (options?.confirm) confirmNext();
        void sendMessage({ parts: [{ type: "text", text }] });
      }}
    />
  );
}

/**
 * Cost and — under the newest answer to a yes/no question — the confirm
 * buttons (`quickReplies.js`), plus the registered message footers
 * (`views/messageFooters.js`) once this answer is stored.
 */
function MetaPart({
  meta,
  message,
  conversationId,
  confirmNext,
  speechCostUsd,
  costOverrideUsd,
}: {
  meta: AgentMetaPart;
  message: ChatMessage;
  conversationId: string | null;
  confirmNext: () => void;
  /** This answer's speech cost tracked client-side this session, once booking confirmed it (undefined: never read aloud, or not booked yet). */
  speechCostUsd: number | undefined;
  /** A live cost for this stored answer, once known (`AgentPanel`'s `costOverrideByMessageId`) — takes precedence over `meta.costUsd`, frozen at `AgentChatBox`'s mount (`resolveAnswerCostUsd`). `undefined`: no override recorded, use `meta.costUsd` as-is. */
  costOverrideUsd: number | null | undefined;
}) {
  const { t } = useTranslate(["Assistant", "Global"]);
  const { getFormattedFloat } = useFormatting();
  const { messages, isStreaming, sendMessage } = useChat();
  const isNewest = messages[messages.length - 1]?.id === message.id;
  const text = message.parts
    .flatMap(p => (p.type === "text" ? [p.text] : []))
    .join("\n\n");
  const views = message.parts.flatMap(p => {
    const view = viewOf(p);
    return view ? [view.component] : [];
  });
  const yesNo = isNewest && !isStreaming && offersYesNo(text, views);
  const risky = yesNo ? riskyQuestion(text) : null;
  const say = (reply: string) =>
    void sendMessage({ parts: [{ type: "text", text: reply }] });

  return (
    <>
      {yesNo &&
        (risky ? (
          <ChatConfirmation
            message={risky}
            confirmLabel={t("Yes", { ns: "Global" })}
            cancelLabel={t("No", { ns: "Global" })}
            onConfirm={() => {
              confirmNext();
              say(t("Yes", { ns: "Global" }));
            }}
            onCancel={() => say(t("No", { ns: "Global" }))}
            sx={{ my: 0.5, p: 1, gap: 0.5 }}
          />
        ) : (
          <Stack direction="row" spacing={1} sx={{ my: 0.5 }}>
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                confirmNext();
                say(t("Yes", { ns: "Global" }));
              }}
            >
              {t("Yes", { ns: "Global" })}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => say(t("No", { ns: "Global" }))}
            >
              {t("No", { ns: "Global" })}
            </Button>
          </Stack>
        ))}
      {(() => {
        const cost = splitAnswerCost(
          resolveAnswerCostUsd(meta, costOverrideUsd),
          resolveStoredSpeechCostUsd(
            meta.costUsd,
            costOverrideUsd,
            speechCostUsd ?? null
          ),
          speechCostUsd ?? null
        );
        if (cost.totalUsd === null) return null;
        return (
          <Tooltip
            title={t(
              "{model} · {promptTokens} prompt, {completionTokens} completion tokens",
              {
                ns: "Assistant",
                model: meta.model ?? "",
                promptTokens: meta.promptTokens ?? 0,
                completionTokens: meta.completionTokens ?? 0,
              }
            )}
          >
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: "inline-block", mt: 0.25 }}
            >
              {t("{answerCost} ct", {
                ns: "Assistant",
                answerCost: getFormattedFloat(cost.totalUsd * 100),
              })}
              {cost.speechUsd !== null && cost.speechUsd > 0 && (
                <>
                  {" "}
                  {t("(+ {speechCost} ct read aloud)", {
                    ns: "Assistant",
                    speechCost: getFormattedFloat(cost.speechUsd * 100),
                  })}
                </>
              )}
            </Typography>
          </Tooltip>
        );
      })()}
      {meta.messageId &&
        agentMessageFooters.map((Footer, index) => (
          <Footer
            key={index}
            messageId={meta.messageId as string}
            conversationId={conversationId}
          />
        ))}
    </>
  );
}

/**
 * The composer's attach button, followed by a microphone for dictation:
 * filled and pulsing while it listens (an outline otherwise), so whether it
 * is recording is visible at a glance. Sending ends the dictation
 * (`cancel()`, not `stop()`): otherwise the microphone keeps listening and
 * writes the answer being read aloud back into the field. A question this
 * mic dictated arms the next streaming answer's autoread
 * (`SpokenQuestionSentContext`, consumed once the send actually starts the
 * assistant streaming).
 */
function AttachAndDictate(props: ChatComposerAttachButtonProps) {
  const { t } = useTranslate(["Assistant"]);
  const tolgee = useTolgee();
  const { value, setValue } = useChatComposer();
  const spoken = React.useRef(false);
  const markSpoken = React.useCallback(
    (text: string) => {
      setValue(text);
      spoken.current = true;
    },
    [setValue]
  );
  const dictation = useDictation(markSpoken, () => tolgee.getLanguage());
  const onSpokenQuestionSent = React.useContext(SpokenQuestionSentContext);
  const dictationCancelRef = React.useContext(DictationCancelContext);
  const { isStreaming } = useChat();
  const { cancel } = dictation;
  // The send itself ends the dictation (`DictationCancelContext`); this is
  // the safety net for streaming that starts any other way.
  React.useEffect(() => {
    if (!isStreaming) return;
    cancel();
    if (spoken.current) {
      spoken.current = false;
      onSpokenQuestionSent();
    }
  }, [isStreaming, cancel, onSpokenQuestionSent]);
  React.useEffect(() => {
    dictationCancelRef.current = cancel;
    return () => {
      dictationCancelRef.current = () => undefined;
    };
  }, [dictationCancelRef, cancel]);
  return (
    <>
      <StopAnswerButton />
      <ChatComposerAttachButton
        {...props}
        onMouseDown={event => {
          props.onMouseDown?.(event);
          keepComposerFocus(event);
        }}
      />
      {dictation.supported && (
        <Tooltip
          title={dictation.listening ? t("Stop dictation") : t("Dictate")}
        >
          <IconButton
            size="small"
            color={dictation.listening ? "error" : "default"}
            aria-pressed={dictation.listening}
            aria-label={
              dictation.listening ? t("Stop dictation") : t("Dictate")
            }
            onMouseDown={keepComposerFocus}
            onClick={event => {
              if (dictation.listening) dictation.stop();
              else dictation.start(value);
              // Also for the keyboard path (Tab, then Space/Enter): the very
              // next Enter must send, not toggle this button again.
              focusComposer(event.currentTarget);
            }}
            sx={
              dictation.listening
                ? {
                    "@keyframes agentMicPulse": {
                      "0%, 100%": { opacity: 1 },
                      "50%": { opacity: 0.45 },
                    },
                    animation: "agentMicPulse 1.2s ease-in-out infinite",
                  }
                : undefined
            }
          >
            {dictation.listening ? (
              <MicIcon fontSize="small" />
            ) : (
              <MicOffIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      )}
    </>
  );
}

/**
 * The conversation history over the thread, on a panel too narrow to show
 * it beside it (`AgentPanel` decides; see `conversationListLayout`). Opened
 * only on the history button and closed again once a conversation is
 * picked (by click or keyboard), on Esc (the panel itself stays open), on
 * a click beside it and on its own close button — so the thread and its
 * buttons are never covered unless the user asked for the history.
 */
function ConversationOverlay({ onClose }: { onClose: () => void }) {
  const { t } = useTranslate(["Assistant"]);
  const { activeConversationId } = useChat();
  const conversations = useConversations();
  const panelRef = React.useRef<HTMLDivElement>(null);
  // Picking an entry by keyboard (Enter) selects it without the entry's
  // click, so close on any change of the shown conversation too.
  const openedOn = React.useRef(activeConversationId);
  React.useEffect(() => {
    if (activeConversationId !== openedOn.current) onClose();
  }, [activeConversationId, onClose]);
  // Into the overlay, so Esc reaches it; back to where it was once closed.
  React.useLayoutEffect(() => {
    const opener =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    panelRef.current?.focus();
    return () => {
      if (opener && document.contains(opener)) opener.focus();
    };
  }, []);
  const listSlotProps = React.useMemo(
    () => ({
      item: {
        onClick: (event: React.MouseEvent<HTMLElement>) => {
          // Picking the conversation already shown closes it as well. A
          // click inside a dialog opened from an entry (the delete
          // confirmation) bubbles here through React but is not in the
          // entry: it picks nothing.
          if (
            event.target instanceof Node &&
            event.currentTarget.contains(event.target)
          )
            onClose();
        },
      },
    }),
    [onClose]
  );
  return (
    <Box sx={{ position: "absolute", inset: 0, zIndex: 10 }}>
      <Box
        data-testid="agent-conversation-overlay-backdrop"
        aria-hidden="true"
        onClick={onClose}
        sx={{
          position: "absolute",
          inset: 0,
          bgcolor: "action.disabledBackground",
          cursor: "pointer",
        }}
      />
      <Stack
        ref={panelRef}
        role="dialog"
        aria-label={t("Conversation history")}
        tabIndex={-1}
        onKeyDown={event => {
          if (event.key !== "Escape") return;
          // Only the overlay closes, not the panel around it.
          event.stopPropagation();
          onClose();
        }}
        sx={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          width: "min(260px, 85%)",
          bgcolor: "background.paper",
          boxShadow: 8,
          outline: "none",
          overscrollBehavior: "contain",
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            pl: 1.5,
            pr: 0.5,
            py: 0.5,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle2" noWrap sx={{ flex: 1 }}>
            {t("Conversation history")}
          </Typography>
          <Tooltip title={t("Hide conversation history")}>
            <IconButton
              aria-label={t("Hide conversation history")}
              onClick={onClose}
              sx={{ minWidth: 44, minHeight: 44 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        {conversations.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ px: 1.5, py: 2 }}
          >
            {t("No earlier conversations")}
          </Typography>
        ) : (
          <ChatConversationList
            variant="compact"
            slots={conversationListSlots}
            slotProps={listSlotProps}
            sx={{ flex: 1, minHeight: 0, ...historyActionsRevealSx }}
          />
        )}
      </Stack>
    </Box>
  );
}

/**
 * Delete action on each conversation-list entry (with a confirmation). The
 * entry leaves the list at once — nothing lists it again while the delete
 * runs — and comes back in its place if the delete fails.
 */
function ConversationRowActions({
  conversation,
  className,
}: {
  conversation: { id: string; title?: string };
  /** x-chat's `itemActions` class: the hover-only rule (`historyActionsRevealSx`) targets it. */
  className?: string;
}) {
  const { t } = useTranslate(["Assistant", "Global"]);
  const [deleteAgentConversation] = useDeleteAgentConversationMutation();
  const { activeConversationId, setActiveConversation } = useChat();
  const store = useChatStore();
  const { deleting, forgetConversation } = useConversationListState();
  const { setEditing } = React.useContext(ConversationTitleEditContext);
  const reloadConversationList = useReloadConversationList();
  const confirmProcessRef = React.useRef<ConfirmProcessRef>(null);
  const remove = async (event: React.MouseEvent) => {
    event.stopPropagation();
    const confirmed = await confirmProcessRef.current?.startConfirmProcess({
      title: t("Delete conversation"),
      text: t("Delete this conversation? This cannot be undone."),
      confirmButtonLabel: t("Delete", { ns: "Global" }),
      cancelButtonLabel: t("Cancel", { ns: "Global" }),
    });
    if (!confirmed) return;
    const { id } = conversation;
    const removed = store.state.conversationsById[id];
    const position = store.state.conversationIds.indexOf(id);
    deleting.add(id);
    store.removeConversation(id);
    try {
      await deleteAgentConversation({ variables: { id } });
      deleting.delete(id);
      forgetConversation(id);
      if (activeConversationId === id) await setActiveConversation(undefined);
    } catch {
      // Already reported to the user by the app-wide Apollo error link.
      deleting.delete(id);
      if (removed && !store.state.conversationsById[id]) {
        const entries = store.state.conversationIds.flatMap(existing => {
          const entry = store.state.conversationsById[existing];
          return entry ? [entry] : [];
        });
        entries.splice(Math.max(position, 0), 0, removed);
        store.setConversations(entries);
      }
    }
    // Either way, show what the server now has — after a failure the delete
    // may still have gone through, or the entry may already be gone.
    try {
      await reloadConversationList();
    } catch (err) {
      Sentry.captureException(err);
    }
  };
  return (
    <Box component="span" className={className}>
      <Tooltip title={t("Rename chat")}>
        <IconButton
          size="small"
          aria-label={t("Rename chat")}
          onClick={event => {
            event.stopPropagation();
            setEditing({ id: conversation.id, where: "row" });
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <ConfirmProcess ref={confirmProcessRef} />
      <Tooltip title={t("Delete conversation")}>
        <IconButton
          size="small"
          aria-label={t("Delete conversation")}
          onClick={event => void remove(event)}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

/** The history entries' own parts, in the sidebar and the narrow panel's overlay alike. */
const conversationListSlots = {
  itemActions: ConversationRowActions,
  title: ConversationTitleWithStatus,
};

/**
 * Renames optimistically in the x-chat store (the one the history row and the
 * chat header both read, so they stay in sync), remembers the title as set
 * locally so a re-read list cannot undo it, and rolls back with a notice when
 * the mutation fails.
 */
function useRenameConversation(): (
  conversation: ChatConversation,
  rawTitle: string
) => void {
  const { t } = useTranslate(["Assistant"]);
  const store = useChatStore();
  const { enqueueSnackbar } = useSnackbar();
  const [renameMutation] = useRenameAgentConversationMutation();
  const { localTitles } = useConversationListState();
  return React.useCallback(
    (conversation, rawTitle) => {
      const title = rawTitle.trim();
      if (title === "" || title === conversation.title) return;
      const previous = conversation.title;
      localTitles.set(conversation.id, title);
      store.updateConversation(conversation.id, { title });
      renameMutation({ variables: { id: conversation.id, title } }).catch(
        () => {
          localTitles.delete(conversation.id);
          store.updateConversation(conversation.id, { title: previous });
          enqueueSnackbar(t("Could not rename the chat"), {
            variant: "error",
          });
        }
      );
    },
    [store, renameMutation, localTitles, enqueueSnackbar, t]
  );
}

/**
 * The inline title editor (history row and chat header): focused with the
 * text selected; Enter or blur saves, Escape cancels. Clicks and keys stay
 * inside — the surrounding row would otherwise select the conversation.
 */
function InlineTitleInput({
  initialTitle,
  onSave,
  onCancel,
}: {
  initialTitle: string;
  onSave: (title: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslate(["Assistant"]);
  const [value, setValue] = React.useState(initialTitle);
  // Enter/Escape unmount the field, which can still blur it afterwards.
  const finished = React.useRef(false);
  const finish = (save: boolean) => {
    if (finished.current) return;
    finished.current = true;
    if (save) onSave(value);
    else onCancel();
  };
  const stop = (event: React.SyntheticEvent) => event.stopPropagation();
  return (
    <TextField
      value={value}
      onChange={event => setValue(event.target.value)}
      autoFocus
      fullWidth
      size="small"
      variant="standard"
      onClick={stop}
      onMouseDown={stop}
      onKeyUp={stop}
      onKeyDown={event => {
        event.stopPropagation();
        if (event.key === "Enter") {
          event.preventDefault();
          finish(true);
        } else if (event.key === "Escape") finish(false);
      }}
      onFocus={event => event.target.select()}
      onBlur={() => finish(true)}
      slotProps={{
        htmlInput: { "aria-label": t("Rename chat"), maxLength: 120 },
      }}
      sx={{ flex: 1, minWidth: 0 }}
    />
  );
}

/** The inline editor for one conversation's title, wired to the shared rename. */
function ConversationTitleEditor({
  conversation,
}: {
  conversation: ChatConversation;
}) {
  const rename = useRenameConversation();
  const { setEditing } = React.useContext(ConversationTitleEditContext);
  return (
    <InlineTitleInput
      initialTitle={conversation.title ?? ""}
      onSave={title => {
        setEditing(null);
        rename(conversation, title);
      }}
      onCancel={() => setEditing(null)}
    />
  );
}

/**
 * The chat header's title (`conversationTitle` slot): click it to rename the
 * open conversation in place.
 */
function ConversationHeaderTitle() {
  const { t } = useTranslate(["Assistant"]);
  const { conversations, activeConversationId } = useChat();
  const { editing, setEditing } = React.useContext(
    ConversationTitleEditContext
  );
  const conversation = conversations.find(c => c.id === activeConversationId);
  if (!conversation) return null;
  if (editing?.id === conversation.id && editing.where === "header")
    return <ConversationTitleEditor conversation={conversation} />;
  return (
    <Tooltip title={t("Rename chat")}>
      <Box
        component="button"
        type="button"
        aria-label={t("Rename chat")}
        onClick={() => setEditing({ id: conversation.id, where: "header" })}
        sx={{
          all: "unset",
          cursor: "text",
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          minWidth: 0,
          typography: "subtitle2",
          "&:focus-visible": { outline: 2, outlineStyle: "solid" },
        }}
      >
        <Typography variant="subtitle2" noWrap component="span">
          {conversation.title ?? conversation.id}
        </Typography>
        <EditIcon sx={{ fontSize: 14, color: "text.disabled" }} />
      </Box>
    </Tooltip>
  );
}

/**
 * The library's own working indicator, with the product's quiet status
 * line (`AgentWorkingStatus`) inside it — it decides where/when to show
 * (a trailing row before the answer starts, then inside the bubble while it
 * streams; hidden once the answer itself is visibly streaming so the line
 * does not show twice).
 */
function WorkingIndicator(props: StreamingIndicatorProps) {
  const { messages } = useChat();
  const last = messages[messages.length - 1];
  const answerStreaming =
    last?.role === "assistant" && last.status === "streaming";
  if (props.items !== undefined && answerStreaming) return null;
  return (
    <StreamingIndicator {...props}>
      <AgentWorkingStatus />
    </StreamingIndicator>
  );
}

function EmptyChatSuggestions(props: ChatSuggestionsProps) {
  const { messages } = useChat();
  const { value } = useChatComposer();
  if (messages.length > 0 || value.trim() !== "") return null;
  return <ChatSuggestions {...props} />;
}

function describeRejection(
  rejection: ChatAttachmentRejection,
  t: ReturnType<typeof useTranslate>["t"]
): string {
  if (rejection.reason === "file-size")
    return t("{fileName} is too large (max. 8 MB)", {
      ns: "Assistant",
      fileName: rejection.file.name,
    });
  if (rejection.reason === "file-count")
    return t("Only {count} files per message", {
      ns: "Assistant",
      count: MAX_ATTACHMENTS,
    });
  return rejection.file.name;
}

/** The notice for rejected attachments — rendered by `AgentPanel` from the rejections themselves, so it follows a language switch. */
export function describeRejections(
  rejections: readonly ChatAttachmentRejection[],
  t: ReturnType<typeof useTranslate>["t"]
): string {
  return rejections
    .map(r => describeRejection(r, t))
    .filter((line, i, all) => all.indexOf(line) === i)
    .join(" · ");
}

/**
 * The slice of `useAgentChat()`'s return value this box needs — derived
 * from the real hook (not hand-duplicated) so its `send`/`openConversation`
 * signatures can never drift from `useAgentChat.ts`.
 */
export type AgentChatApi = Pick<
  ReturnType<typeof useAgentChat>,
  | "messages"
  | "conversationId"
  | "isLoading"
  | "stopping"
  | "send"
  | "stop"
  | "openConversation"
  | "forgetConversation"
  | "clearRetryable"
  | "keepFailedMessage"
  | "backgroundTurns"
>;

/**
 * History rows show their delete button on hover or keyboard focus only
 * (focus-within keeps it reachable by keyboard); touch devices have no
 * hover, so it stays visible there.
 */
export const historyActionsRevealSx = {
  [`& .${chatConversationListClasses.itemActions}`]: {
    opacity: 0,
    "@media (hover: none)": { opacity: 1 },
  },
  [`& .${chatConversationListClasses.item}:hover .${chatConversationListClasses.itemActions}, & .${chatConversationListClasses.item}:focus-within .${chatConversationListClasses.itemActions}`]:
    { opacity: 1 },
};

/**
 * `ChatBox`'s own `sx`, pulled out so its box-sizing rule (see the comment
 * inline below) can be asserted directly in a unit test without rendering
 * `ChatBox` and relying on jsdom's CSS cascade.
 */
const chatBoxBaseSx = {
  flex: 1,
  minHeight: 0,
  border: 0,
  fontSize: "0.875rem",
  [`& .${chatMessageClasses.root}`]: { py: 0.25 },
  // ChatBox sets `* { box-sizing: inherit }` (border-box) with the same
  // specificity as MUI's own `content-box` on inputs, so whichever style
  // Emotion inserted last wins: after a text field elsewhere on the page
  // rendered first, the form fields in a message collapse to their padding.
  // Restore MUI's value with a more specific rule.
  [`& .${chatMessageClasses.content} .MuiInputBase-input`]: {
    boxSizing: "content-box",
  },
  [`& .${chatMessageClasses.bubble}`]: {
    maxWidth: "100%",
    borderRadius: 1,
  },
  [`& .${chatMessageClasses.roleAssistant} .${chatMessageClasses.bubble}`]: {
    bgcolor: "transparent",
    color: "text.primary",
    px: 0,
    py: 0.5,
  },
  [`& .${chatMessageClasses.roleUser}`]: { justifyContent: "stretch" },
  [`& .${chatMessageClasses.roleUser} .${chatMessageClasses.content}`]: {
    width: "100%",
    maxWidth: "100%",
  },
  [`& .${chatMessageClasses.roleUser} .${chatMessageClasses.bubble}`]: {
    width: "100%",
    bgcolor: agentUserBubbleBackground,
    color: "text.primary",
    px: 1.25,
    py: 0.75,
  },
} satisfies SxProps<Theme>;

export const chatBoxSx = Object.assign(
  {},
  chatBoxBaseSx,
  historyActionsRevealSx
);

export function AgentChatBox({
  chat,
  context,
  outbox,
  onAttachmentsRejected,
  showConversations,
  conversationOverlayOpen,
  onConversationOverlayClose,
  focusComposerOnMount,
  onConversation,
  speech,
  speechCostByMessageId,
  costOverrideByMessageId,
}: {
  chat: AgentChatApi;
  context: PageContext;
  outbox: { current: AgentChatOutbox };
  /** Attachments the composer refused (too large, too many); `AgentPanel` shows them as a notice. */
  onAttachmentsRejected: (rejections: ChatAttachmentRejection[]) => void;
  /** The history beside the thread (a wide enough panel, and shown there). */
  showConversations: boolean;
  /** The history over the thread (a narrow panel, opened on its history button) — see `ConversationOverlay`. */
  conversationOverlayOpen: boolean;
  onConversationOverlayClose: () => void;
  /** Focuses the composer once, as the box mounts (a new conversation, so the user can type at once). */
  focusComposerOnMount: boolean;
  onConversation: (conversationId: string | null) => void;
  speech: SpeechOutput;
  /** This session's booked speech cost per stored answer (`AgentPanel`'s `onCost` bookkeeping). */
  speechCostByMessageId: Record<string, number>;
  /** A live cost per stored answer, once known (`AgentPanel`, kept in sync with `chat.messages`) — read by `MetaPart` in place of the frozen `meta.costUsd` `initialMessages` seeded at mount (`resolveAnswerCostUsd`). */
  costOverrideByMessageId: Record<string, number | null>;
}) {
  const { t } = useTranslate(["Assistant"]);
  const { getFormattedDateTime } = useFormatting();
  const localeText = useAgentChatLocaleText();
  const toolTitle = useAgentToolTitle();
  const apolloClient = useApolloClient();
  const [requestAttachmentUploadUrl] =
    useRequestAgentAttachmentUploadUrlMutation();
  const confirm = React.useRef(false);
  const chatBoxRef = React.useRef<HTMLDivElement>(null);
  // Only the value at mount counts: later focus is the user's.
  const focusOnMount = React.useRef(focusComposerOnMount);
  // After the mount's own focus moves (a closing history overlay hands the
  // focus back to its opener), hence an effect rather than `autoFocus`.
  React.useEffect(() => {
    if (focusOnMount.current)
      chatBoxRef.current
        ?.querySelector<HTMLTextAreaElement>("textarea")
        ?.focus();
  }, []);
  const storeRef = React.useRef<ChatStore | null>(null);
  const { backgroundTurns } = chat;
  // Re-renders the running markers whenever a turn starts, moves or ends.
  React.useSyncExternalStore(
    backgroundTurns.subscribe,
    backgroundTurns.getVersion
  );
  // The server's `running` flags from the last list load — for a chat
  // running where this panel does not read it (e.g. another tab).
  const [serverRunning, setServerRunning] = React.useState<ReadonlySet<string>>(
    () => new Set()
  );
  const editingRef = React.useRef<ConversationTitleEdit | null>(null);
  const [editing, setEditingState] =
    React.useState<ConversationTitleEdit | null>(null);
  const titleEdit = React.useMemo(
    () => ({
      editing,
      setEditing: (edit: ConversationTitleEdit | null) => {
        editingRef.current = edit;
        setEditingState(edit);
      },
    }),
    [editing]
  );
  const listState = React.useMemo<ConversationListState>(
    () => ({
      backgroundTurns,
      deleting: new Set(),
      localTitles: new Map(),
      editingRef,
      forgetConversation: conversationId =>
        latest.current.chat.forgetConversation(conversationId),
      // Kept as is when unchanged: every list load reports, and a new set
      // each time would re-render the box for nothing.
      reportServerRunning: next =>
        setServerRunning(current =>
          current.size === next.size &&
          Array.from(next).every(id => current.has(id))
            ? current
            : next
        ),
    }),
    [backgroundTurns]
  );
  const conversationStatus = React.useCallback(
    (conversationId: string): ConversationStatus => {
      if (
        backgroundTurns.isRunning(conversationId) ||
        (serverRunning.has(conversationId) &&
          !backgroundTurns.finishedHere(conversationId))
      )
        return "running";
      return backgroundTurns.finishedUnopened(conversationId)
        ? "finished"
        : null;
    },
    // The registry is mutable: re-evaluated on every render (above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [backgroundTurns, serverRunning, backgroundTurns.getVersion()]
  );
  const suggestions = useAgentStarterSuggestions(context, agentStarterRegistry);
  const shownTurn = React.useMemo<ShownTurnControls>(
    () => ({
      running: chat.isLoading,
      stopping: chat.stopping,
      stop: chat.stop,
    }),
    [chat.isLoading, chat.stopping, chat.stop]
  );
  // Read by the adapter (built once, below) at the moment it needs each
  // value — so a text it produces (e.g. the empty-answer fallback) is in
  // the language shown then, not the one shown at mount.
  const latest = React.useRef({
    chat,
    context,
    getFormattedDateTime,
    requestAttachmentUploadUrl,
    t,
    toolTitle,
    listState,
  });
  latest.current = {
    chat,
    context,
    getFormattedDateTime,
    requestAttachmentUploadUrl,
    t,
    toolTitle,
    listState,
  };
  // Read at the moment a background answer is rebuilt, so it is in the
  // language shown then.
  const toolTitleNow = React.useCallback(() => latest.current.toolTitle, []);
  const emptyReplyTextNow = React.useCallback(
    () => latest.current.t("(no answer)"),
    []
  );
  // Armed at send-time when the question was dictated; consumed once by
  // `SpeechAutoReadSync` for the next streaming answer.
  const autoReadNext = React.useRef(false);
  // On by default per chat: a spoken question's answer is read aloud.
  // Turned off by pausing precisely the answer autoread is reading (via its
  // own "Read aloud" action); turned back on by starting "Read aloud" on
  // any message, or a new chat/conversation.
  const autoReadEnabled = React.useRef(true);
  // Which message id autoread is currently (or was last) reading, so
  // pausing it — and only it — is what turns autoread off.
  const autoReadSessionKey = React.useRef<string | null>(null);
  const onSpokenQuestionSent = React.useCallback(() => {
    if (autoReadEnabled.current) autoReadNext.current = true;
  }, []);
  // The running dictation's `cancel`, kept current by `AttachAndDictate`.
  const dictationCancel = React.useRef<() => void>(() => undefined);

  const adapter = React.useMemo(
    () =>
      createAgentChatAdapter({
        send: (content, options) => latest.current.chat.send(content, options),
        context: () => latest.current.context,
        takeConfirm: () => {
          const value = confirm.current;
          confirm.current = false;
          return value;
        },
        requestAttachmentUploadUrl: async input => {
          const { data } = await latest.current.requestAttachmentUploadUrl({
            variables: { input },
          });
          if (!data)
            throw new Error(`Could not upload the file ${input.fileName}.`);
          return data.requestAgentAttachmentUploadUrl;
        },
        emptyReplyText: () => latest.current.t("(no answer)"),
        onSend: () => dictationCancel.current(),
        toolTitle: call => latest.current.toolTitle(call),
        onConflict: ({ messageId, text }) => {
          // Refused while the previous turn still runs: nothing was sent, so
          // take the optimistic message back out of the thread and hand the
          // text back to the composer for the user to send again later (no
          // automatic retry). `AgentPanel` shows the notice (`chat.conflict`).
          const store = storeRef.current;
          if (!store) return;
          store.removeMessage(messageId);
          if (store.state.composerValue === "") store.setComposerValue(text);
        },
        onFailed: ({ messageId, assistantMessageId, text, files }) => {
          // The turn failed without a stored answer: like a conflict, take
          // the user's message back out of the thread (and the failed answer
          // with it) and hand the text and the attachments back to the
          // composer (as if the user added the files again). `AgentPanel`
          // shows the error with its retry action (`chat.error`).
          const store = storeRef.current;
          if (!store) return;
          store.removeMessage(assistantMessageId);
          // `AgentPanel` offers Retry only while the failed message is back
          // in the composer (it resubmits whatever the composer holds) — the
          // chat may have put it back itself already, so check first.
          // Exactly the failed message's files: one attached meanwhile would
          // otherwise go along with the retry.
          const composerText = store.state.composerValue;
          const attached = store.state.composerAttachments;
          const textBack = composerText === text;
          const filesBack =
            attached.length === files.length &&
            files.every(file => attached.some(a => a.file === file));
          const textFits = textBack || composerText === "";
          const filesFit =
            files.length === 0 || filesBack || attached.length === 0;
          if (!textFits || !filesFit) {
            // Typed or attached something else meanwhile: the message cannot
            // go back without overwriting it, so it stays in the thread
            // instead of being lost (in the chat state too, so the next
            // request carries it), and there is nothing to retry.
            latest.current.chat.keepFailedMessage();
            return;
          }
          store.removeMessage(messageId);
          if (!textBack) store.setComposerValue(text);
          if (!filesBack && files.length > 0) outbox.current.addFiles?.(files);
          // Back in full, but beside a file attached meanwhile: a retry would
          // send that file along.
          if (files.length === 0 && attached.length > 0)
            latest.current.chat.clearRetryable();
        },
      }),
    []
  );
  // Seeded once from `useAgentChat`'s own state at mount time (a live
  // session or an already-opened history) — captured via `useState`'s
  // initializer, not recomputed every render, so a later `chat.messages`
  // change (e.g. `AgentPanel`'s `onStoredReplayFinished` re-opening the same
  // conversation just to refresh a message's cost) does not feed `ChatBox` a
  // new `initialMessages` value after it already initialized from this one —
  // MUI X Chat warns ("changing the default messages state of an
  // uncontrolled ChatProvider") when an "initial" prop changes post-mount.
  const [initialMessages] = React.useState(() =>
    toChatMessages(chat.messages, toolTitle)
  );

  // `listConversations`/`listMessages` need `apolloClient`/`chat.openConversation`,
  // stable identities MUI X Chat is not expected to tolerate changing every
  // render — attached once, reading through `latest`/a ref instead.
  const adapterRef = React.useRef(adapter);
  adapterRef.current = adapter;
  const fullAdapter = React.useMemo(() => {
    const base = adapterRef.current;
    return {
      ...base,
      async listConversations() {
        return {
          conversations: await fetchConversationList(
            apolloClient,
            latest.current.getFormattedDateTime,
            latest.current.listState
          ),
        };
      },
      async listMessages({ conversationId }: { conversationId: string }) {
        const {
          chat: current,
          toolTitle: title,
          t: translate,
        } = latest.current;
        const messages = (await current.openConversation(conversationId)) ?? [];
        const stored = toChatMessages(messages, title);
        // Still being answered in the background: its answer so far goes
        // last, and `BackgroundTurnsSync` keeps it growing.
        const running = current.backgroundTurns.attached();
        return {
          messages:
            running && running.conversationId === conversationId
              ? [
                  ...stored,
                  runningAnswerMessage(running, title, () =>
                    translate("(no answer)")
                  ),
                ]
              : stored,
        };
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apolloClient]);

  const partRenderers: ChatPartRendererMap = React.useMemo(
    () => ({
      text: ({ part, message, index }) =>
        message.role === "assistant" ? (
          message.parts
            .slice(index + 1)
            .some(p => p.type === DISCARD_PART) ? null : (
            <AgentMarkdown content={stripLeadingBreak(part.text)} />
          )
        ) : (
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {agentUserMessageText(part.text, t)}
          </Typography>
        ),
      [DISCARD_PART]: () => null,
      [VIEW_PART]: ({ part, message }) => (
        <ViewPart
          view={viewOf(part)}
          message={message}
          confirmNext={() => {
            confirm.current = true;
          }}
        />
      ),
      [META_PART]: ({ part, message }) => {
        const meta = metaOf(message);
        return meta ? (
          <MetaPart
            meta={meta}
            message={message}
            conversationId={chat.conversationId}
            confirmNext={() => {
              confirm.current = true;
            }}
            speechCostUsd={
              meta.messageId ? speechCostByMessageId[meta.messageId] : undefined
            }
            costOverrideUsd={
              meta.messageId
                ? costOverrideByMessageId[meta.messageId]
                : undefined
            }
          />
        ) : null;
      },
    }),
    [chat.conversationId, speechCostByMessageId, costOverrideByMessageId, t]
  );

  const messageActions = React.useCallback(
    (actionContext: ChatMessageActionsResolveContext) => {
      if (actionContext.role !== "assistant" || !speech.available)
        return { extraActions: [] };
      const message = actionContext.message;
      // The chat message's own id — identifies this message's reading
      // regardless of whether it is already stored (`metaOf`'s `messageId`,
      // only known once persisted); `isActiveKey` also knows the stored id
      // as the same reading once learned, so a message listed under it
      // after a reload still pauses instead of restarting.
      const key = actionContext.messageId;
      const persistedMessageId = message
        ? (metaOf(message)?.messageId ?? null)
        : null;
      const isActive = speech.isActiveKey(key);
      const action = messageSpeechAction(isActive, speech.state);
      const labelKind = messageSpeechLabelKind(isActive, speech.state);

      const label =
        labelKind === "pause"
          ? t("Pause reading aloud")
          : labelKind === "resume"
            ? t("Resume reading aloud")
            : labelKind === "read-again"
              ? t("Read aloud again")
              : t("Read aloud");
      const icon =
        labelKind === "pause" ? (
          <PauseIcon fontSize="small" />
        ) : labelKind === "read-again" ? (
          <ReplayIcon fontSize="small" />
        ) : (
          <PlayArrowIcon fontSize="small" />
        );
      // Neither a stored answer nor the one currently streaming: nothing to read yet.
      const disabled =
        !isActive && !persistedMessageId && !actionContext.streaming;
      const onClick = () => {
        if (action === "pause") {
          // Interrupting precisely the answer autoread started: later
          // dictated questions are not read aloud until "Read aloud" is
          // started again. Compared through `isActiveKey`: the id autoread
          // recorded may be the other name of this same reading by now.
          if (
            autoReadSessionKey.current !== null &&
            speech.isActiveKey(autoReadSessionKey.current)
          )
            autoReadEnabled.current = false;
          speech.pause();
        } else if (action === "resume") {
          speech.resume();
        } else {
          autoReadEnabled.current = true;
          autoReadSessionKey.current = null;
          speech.readAloud(
            key,
            message ? visibleText(message) : "",
            persistedMessageId,
            actionContext.streaming
          );
        }
      };

      const extraAction: ChatMessageExtraAction = {
        id: "speech",
        label,
        icon,
        disabled,
        onClick,
      };
      return { extraActions: [extraAction] };
    },
    [speech, t]
  );

  return (
    <ConversationListStateContext.Provider value={listState}>
      <ConversationTitleEditContext.Provider value={titleEdit}>
        <ConversationStatusContext.Provider value={conversationStatus}>
          <ShownTurnContext.Provider value={shownTurn}>
            <SpokenQuestionSentContext.Provider value={onSpokenQuestionSent}>
              <DictationCancelContext.Provider value={dictationCancel}>
                <ChatBox
                  ref={chatBoxRef}
                  adapter={fullAdapter}
                  localeText={localeText}
                  // Deliberately no `initialActiveConversationId`: setting it makes the
                  // runtime treat this as "not yet cached" and call `listMessages`
                  // right after mount, replacing this seed with whatever that resolves
                  // to — see the alpha-API note in AgentChatBox's design write-up.
                  initialMessages={initialMessages}
                  onActiveConversationChange={id => {
                    // A different (or no) conversation starts autoread fresh.
                    autoReadEnabled.current = true;
                    autoReadSessionKey.current = null;
                    onConversation(id ?? null);
                  }}
                  partRenderers={partRenderers}
                  variant="compact"
                  density="compact"
                  // Never MUI X Chat's own narrow layouts: below its width
                  // breakpoints it swaps the thread for the list ("split", the
                  // default 440 px panel) or needs a menu button of its own
                  // ("overlay"). The panel decides where the history goes
                  // (`conversationListLayout`) and shows the overlay itself.
                  layoutMode="standard"
                  slots={{
                    messageAvatar: null,
                    messageAuthorName: null,
                    messageMeta: null,
                    messageInlineMeta: null,
                    dateDivider: null,
                    composerAttachButton: AttachAndDictate,
                    streamingIndicator: WorkingIndicator,
                    suggestions: EmptyChatSuggestions,
                    conversationTitle: ConversationHeaderTitle,
                  }}
                  slotProps={{
                    messageActions,
                    conversationList: {
                      slots: conversationListSlots,
                    },
                  }}
                  suggestions={suggestions}
                  suggestionsAutoSubmit={false}
                  features={{
                    conversationList: showConversations,
                    conversationHeader: true,
                    attachments: {
                      maxFileCount: MAX_ATTACHMENTS,
                      maxFileSize: MAX_ATTACHMENT_BYTES,
                      onAttachmentReject: onAttachmentsRejected,
                    },
                    scrollToBottom: true,
                    helperText: false,
                    suggestions: true,
                    dateDivider: false,
                  }}
                  sx={chatBoxSx}
                >
                  <OutboxBridge outbox={outbox} />
                  <ChatStoreBridge storeRef={storeRef} />
                  <ConversationListLocaleSync
                    enabled={showConversations || conversationOverlayOpen}
                  />
                  {conversationOverlayOpen && (
                    <ConversationOverlay onClose={onConversationOverlayClose} />
                  )}
                  <BackgroundTurnsSync
                    backgroundTurns={backgroundTurns}
                    toolTitle={toolTitleNow}
                    emptyReplyText={emptyReplyTextNow}
                  />
                  <SpeechAutoReadSync
                    speech={speech}
                    autoReadNext={autoReadNext}
                    autoReadSessionKey={autoReadSessionKey}
                  />
                </ChatBox>
              </DictationCancelContext.Provider>
            </SpokenQuestionSentContext.Provider>
          </ShownTurnContext.Provider>
        </ConversationStatusContext.Provider>
      </ConversationTitleEditContext.Provider>
    </ConversationListStateContext.Provider>
  );
}
